import { Mail, Phone, ExternalLink, Check, X } from "lucide-react";

export default function PendingApplications({ pending, onApprove, onReject }) {
  if (!pending || pending.length === 0) return null;

  return (
    <div className="mb-12" data-testid="pending-applications-section">
      <div className="flex items-baseline gap-3 mb-4">
        <p className="text-xs uppercase tracking-[0.25em] text-amber-700">
          {pending.length} new {pending.length === 1 ? "application" : "applications"} waiting
        </p>
        <span className="h-px flex-1 bg-amber-200" />
      </div>

      <div className="space-y-4">
        {pending.map((u) => (
          <article
            key={u.id}
            data-testid={`pending-application-${u.id}`}
            className="rounded-2xl border border-amber-300/70 bg-amber-50/40 p-5 md:p-6"
          >
            <header className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-amber-700 mb-1">Wholesale application</p>
                <h3 className="font-serif text-xl md:text-2xl text-foreground">{u.business_name || "—"}</h3>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {u.contact_name || "—"}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onReject(u)}
                  data-testid={`pending-reject-${u.id}`}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs uppercase tracking-[0.12em] border border-border text-muted-foreground hover:bg-foreground/5 hover:text-destructive transition-colors"
                >
                  <X size={13} strokeWidth={1.6} /> Decline
                </button>
                <button
                  type="button"
                  onClick={() => onApprove(u)}
                  data-testid={`pending-approve-${u.id}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs uppercase tracking-[0.12em] bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
                >
                  <Check size={13} strokeWidth={1.8} /> Approve
                </button>
              </div>
            </header>

            <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm mb-4">
              <Pair label="Email">
                <a href={`mailto:${u.email}`} className="text-primary underline inline-flex items-center gap-1.5">
                  <Mail size={12} strokeWidth={1.6} /> {u.email}
                </a>
              </Pair>
              <Pair label="Phone">
                {u.phone ? (
                  <span className="inline-flex items-center gap-1.5"><Phone size={12} strokeWidth={1.6} />{u.phone}</span>
                ) : "—"}
              </Pair>
              <Pair label="Website">
                {u.website ? (
                  <a href={u.website} target="_blank" rel="noreferrer" className="text-primary underline inline-flex items-center gap-1.5 break-all">
                    <ExternalLink size={12} strokeWidth={1.6} /> {u.website.replace(/^https?:\/\//, "")}
                  </a>
                ) : "—"}
              </Pair>
              <Pair label="Business address" wide>{u.business_address || "—"}</Pair>
              <Pair label="Province">{u.province || "—"}</Pair>
              <Pair label="Store type" wide>{u.store_classifications || "—"}</Pair>
              <Pair label="Brands stocked" wide>{u.brands_stocked || "—"}</Pair>
            </dl>

            {u.message && (
              <div className="rounded-xl border-l-2 border-amber-400 bg-white/70 p-4">
                <p className="text-[10px] uppercase tracking-[0.18em] text-amber-700 mb-1.5">Their message</p>
                <p className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/85">{u.message}</p>
              </div>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}

function Pair({ label, children, wide }) {
  return (
    <div className={wide ? "sm:col-span-2 lg:col-span-3" : ""}>
      <dt className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-0.5">{label}</dt>
      <dd className="text-foreground/90">{children}</dd>
    </div>
  );
}
